import type { DropInSessionResponse } from "@bw/shared/types/generated";

const ISO_INSTANT_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(Z|[+-](\d{2}):(\d{2}))$/;

function parseStrictIsoInstant(value: string): number | null {
  const match = ISO_INSTANT_PATTERN.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[8] === undefined ? 0 : Number(match[8]);
  const offsetMinute = match[9] === undefined ? 0 : Number(match[9]);
  if (
    year < 1 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    hour > 23 ||
    minute > 59 ||
    second > 59 ||
    offsetHour > 23 ||
    offsetMinute > 59
  ) {
    return null;
  }

  const calendarDate = new Date(0);
  calendarDate.setUTCFullYear(year, month - 1, day);
  calendarDate.setUTCHours(hour, minute, second, 0);
  if (
    calendarDate.getUTCFullYear() !== year ||
    calendarDate.getUTCMonth() !== month - 1 ||
    calendarDate.getUTCDate() !== day ||
    calendarDate.getUTCHours() !== hour ||
    calendarDate.getUTCMinutes() !== minute ||
    calendarDate.getUTCSeconds() !== second
  ) {
    return null;
  }

  const instantMs = Date.parse(value);
  return Number.isFinite(instantMs) ? instantMs : null;
}

interface DropInSessionQueryAuthorityState {
  isSuccess: boolean;
  isFetchedAfterMount: boolean;
  error: unknown | null;
  failureReason: unknown | null;
}

/**
 * A cached successful result stops being authorization proof as soon as its
 * refresh observes a failure. TanStack Query keeps `status: "success"` and
 * `error: null` while an automatic retry is pending, but exposes that first
 * failure through `failureReason`; fail closed until a later read succeeds.
 */
export function dropInSessionSnapshotIsAuthoritative(
  query: DropInSessionQueryAuthorityState,
): boolean {
  return (
    query.isSuccess &&
    query.isFetchedAfterMount &&
    query.error === null &&
    query.failureReason === null
  );
}

/**
 * Return the authoritative deadline only while this exact facility session is
 * currently active. Missing, malformed, mismatched, and elapsed snapshots all
 * fail closed so callers cannot accidentally turn cached session data into
 * court-state authority.
 */
export function activeDropInAuthorizationExpiresAt(
  session: DropInSessionResponse | null | undefined,
  facilityId: string,
  nowMs: number = Date.now(),
): string | null {
  if (
    !facilityId ||
    session?.status !== "active" ||
    session.facility_id !== facilityId ||
    typeof session.expires_at !== "string"
  ) {
    return null;
  }

  const expiresAtMs = parseStrictIsoInstant(session.expires_at);
  return expiresAtMs !== null && nowMs < expiresAtMs
    ? session.expires_at
    : null;
}
